import { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Droplet, Fish, Leaf, Flower2, ClipboardList, LifeBuoy,
  BadgeCheck, ArrowRight, ArrowLeft, Play, Pause, Volume2, VolumeX,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";

const SERVICES = [
  { icon: ShieldCheck, title: "Service & Maintenance", to: "/maintenance", body: "Keep your aquarium healthy and beautiful with customized maintenance programs, scheduled service visits, water testing, equipment inspections, and white-glove care." },
  { icon: Droplet, title: "Aquarium Water Delivery", to: "/water-delivery", body: "Receive professionally prepared RODI water delivered directly to your home or business, ensuring exceptional water quality for freshwater, saltwater, and reef aquariums." },
  { icon: Fish, title: "Saltwater Aquariums", to: "/saltwater", body: "Professionally designed marine aquariums featuring vibrant fish, live rock, advanced filtration, and customized maintenance programs built for long-term success." },
  { icon: Leaf, title: "Freshwater Aquariums", to: "/freshwater", body: "Natural freshwater aquariums designed with lush aquatic plants, colorful fish, custom aquascapes, and professional installation for lasting enjoyment." },
  { icon: Flower2, title: "Reef Aquariums", to: "/reef", body: "Luxury reef aquarium systems featuring vibrant corals, reef-safe fish, premium lighting, advanced filtration, and expert long-term maintenance." },
  { icon: ClipboardList, title: "Aquarium Design & Installation", to: "/design", body: "From concept to completion, Seatech designs and installs custom aquariums that seamlessly integrate into homes, offices, restaurants, and commercial spaces." },
  { icon: LifeBuoy, title: "24/7 Emergency Services", to: "/emergency", body: "Active maintenance clients receive exclusive access to around-the-clock emergency aquarium support whenever unexpected issues arise." },
  { icon: BadgeCheck, title: "6-Month Guarantee", to: "/guarantee", body: "Every professionally installed aquarium is backed by Seatech's commitment to quality workmanship and long-term success, giving you added confidence in your investment." },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Bubbles = () => {
  const bubbles = useMemo(
    () => Array.from({ length: 16 }).map((_, i) => ({
      id: i, left: Math.random() * 100, size: 5 + Math.random() * 20,
      duration: 11 + Math.random() * 12, delay: Math.random() * 10,
    })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {bubbles.map((b) => (
        <motion.span
          key={b.id}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: "-15%", opacity: [0, 0.5, 0.5, 0] }}
          transition={{ duration: b.duration, delay: b.delay, repeat: Infinity, ease: "linear" }}
          className="absolute rounded-full border border-[#38D6E8]/30 bg-[#38D6E8]/10 backdrop-blur-[1px]"
          style={{ left: `${b.left}%`, width: b.size, height: b.size }}
        />
      ))}
    </div>
  );
};

const LightRays = () => (
  <div className="pointer-events-none fixed inset-0 overflow-hidden">
    {[16, 48, 78].map((left, i) => (
      <motion.div
        key={i}
        animate={{ opacity: [0.04, 0.14, 0.04] }}
        transition={{ duration: 9 + i * 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-1/3 h-[160%] w-44 rotate-12 bg-gradient-to-b from-[#7cebf6]/40 via-[#38D6E8]/10 to-transparent blur-2xl"
        style={{ left: `${left}%` }}
      />
    ))}
  </div>
);

export default function ServicesOverview() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted && v.paused) { v.play(); setPlaying(true); }
  };

  const openService = (to) => {
    if (to.startsWith("/")) {
      navigate(to);
    } else {
      navigate("/", { state: { scrollTarget: to } });
    }
  };

  return (
    <main data-testid="services-page" className="relative min-h-screen bg-[#071A29] text-[#F7FAFC]">
      {/* Fixed looping background */}
      <div className="fixed inset-0 z-0">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src="/media/hero-reef.mp4"
          autoPlay loop muted playsInline
          data-testid="services-bg-video"
        />
        <div className="absolute inset-0 bg-[#071A29]/82" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#04101c]/85 via-[#071A29]/72 to-[#04101c]/94" />
      </div>

      <LightRays />

      <div className="relative z-10">
        <Navbar />

        {/* HERO */}
        <section className="relative overflow-hidden px-6 lg:px-16 pt-52 pb-12">
          <Bubbles />
          <div className="relative mx-auto w-full max-w-6xl text-center">
            <Reveal delay={0.05}>
              <h1 className="font-title text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-6xl xl:text-7xl whitespace-nowrap" data-testid="services-headline">
                Professional Aquarium Services
              </h1>
            </Reveal>
          </div>
        </section>

        {/* SERVICES GRID */}
        <section className="px-6 lg:px-16 pb-20 pt-6">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {SERVICES.map((s, i) => (
                <Reveal key={s.title} delay={(i % 4) * 0.08}>
                  <button
                    onClick={() => openService(s.to)}
                    data-testid={`services-card-${i}`}
                    className="group flex h-full w-full flex-col rounded-[20px] border border-[#F7FAFC]/12 bg-[#0F4C81]/20 p-7 text-left backdrop-blur-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-[1.03] hover:border-[#38D6E8]/50 hover:bg-[#0F4C81]/30 hover:shadow-[0_24px_70px_-30px_#38D6E8]"
                  >
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 4 + (i % 3), repeat: Infinity, ease: "easeInOut" }}
                      className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[#38D6E8]/25 bg-[#38D6E8]/10 text-[#7cebf6] transition-all duration-500 group-hover:shadow-[0_0_28px_-6px_#38D6E8]"
                    >
                      <s.icon className="h-7 w-7" strokeWidth={1.5} />
                    </motion.div>
                    <h3 className="mt-6 font-title text-lg font-bold leading-snug tracking-tight">{s.title}</h3>
                    <p className="mt-3 flex-1 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{s.body}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#38D6E8]">
                      Learn More
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative px-6 lg:px-16 py-32">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#03101d]/80 to-[#03101d]/95" />
          <div className="relative mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
            <div>
              <Reveal>
                <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl" data-testid="services-cta-heading">
                  Let's Build Something Extraordinary
                </h2>
                <p className="mt-6 max-w-2xl font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">
                  Whether you're starting your first aquarium or upgrading an existing display, Seatech
                  Aquariums delivers expert design, installation, maintenance, and ongoing support
                  tailored to your unique vision.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => openService("#contact")}
                    data-testid="services-cta-primary"
                    className="inline-flex items-center gap-3 rounded-full bg-[#38D6E8] px-9 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04212a] transition-all duration-300 hover:shadow-[0_0_44px_-8px_#38D6E8]"
                  >
                    Schedule Your Consultation
                  </button>
                  <button
                    onClick={() => openService("#contact")}
                    data-testid="services-cta-secondary"
                    className="inline-flex items-center gap-2 rounded-full border border-[#F7FAFC]/25 px-8 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[#F7FAFC]/90 backdrop-blur-md transition-all duration-300 hover:border-[#F7FAFC]/60"
                  >
                    Contact Seatech
                  </button>
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <button
                  onClick={() => navigate("/")}
                  data-testid="services-back-home"
                  className="mt-10 inline-flex items-center gap-2 font-body text-sm font-light uppercase tracking-[0.16em] text-[#F7FAFC]/60 transition-colors hover:text-[#F7FAFC]"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to Site
                </button>
              </Reveal>
            </div>

            <Reveal delay={0.2}>
              <div
                className="group relative aspect-video overflow-hidden rounded-[20px] border border-[#F7FAFC]/12 bg-[#0F4C81]/20 shadow-[0_24px_70px_-30px_#38D6E8] backdrop-blur-2xl"
                data-testid="why-seatech-video-window"
              >
                <video
                  ref={videoRef}
                  className="h-full w-full object-cover"
                  src="/media/why-seatech.mp4"
                  autoPlay loop muted playsInline
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  data-testid="why-seatech-video"
                />
                <div className="pointer-events-none absolute inset-0 rounded-[20px] ring-1 ring-inset ring-[#38D6E8]/20" />

                {/* Controls */}
                <div className="absolute bottom-3 right-3 flex items-center gap-2 opacity-90 transition-opacity duration-300 group-hover:opacity-100">
                  <button
                    onClick={togglePlay}
                    aria-label={playing ? "Pause video" : "Play video"}
                    data-testid="why-seatech-playpause"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[#F7FAFC]/25 bg-[#03101d]/70 text-[#F7FAFC] backdrop-blur-md transition-all duration-300 hover:border-[#38D6E8]/60 hover:text-[#38D6E8]"
                  >
                    {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={toggleMute}
                    aria-label={muted ? "Unmute video" : "Mute video"}
                    data-testid="why-seatech-mute"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[#F7FAFC]/25 bg-[#03101d]/70 text-[#F7FAFC] backdrop-blur-md transition-all duration-300 hover:border-[#38D6E8]/60 hover:text-[#38D6E8]"
                  >
                    {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </main>
  );
}
